import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, supabaseUrl } from '@/lib/supabase';
import type { PetSize } from '@/types';

export interface PortalPetDto {
  id: string;
  name: string;
  species: string;
  breed: string | null;
  size: string | null;
  photoUrl: string | null;
  birthDate: string | null;
  isSterilized: boolean;
}

export interface PortalNextReservation {
  kind: 'grooming' | 'boarding';
  id: string;
  startAt: string;
  petName: string;
  suiteName?: string;
}

export interface PortalTimeline {
  pet: {
    id: string; name: string; species: string; breed: string | null;
    size: string | null; photoUrl: string | null; birthDate: string | null;
  };
  upcomingAppointments: Array<{
    id: string; startAt: string; status: string; serviceName: string | null;
  }>;
  history: Array<{
    id: string; performedAt: string; groomerName: string;
    totalCents: number; notes: string | null;
    items: Array<{ name: string; priceCents: number }>;
  }>;
  upcomingStays: Array<{
    id: string; checkInAt: string; checkOutAt: string;
    status: string; suiteName: string; suiteTier: string;
  }>;
  /**
   * VetCare: solo lo FIRMADO y solo lo que es para el dueño. Las notas
   * internas y el diagnóstico en estudio no viajan (los filtra la base).
   * `null` si la clínica no tiene VetCare.
   */
  clinica: {
    consultas: Array<{
      id: string; fecha: string; motivo: string; veterinario: string | null;
      pesoKg: number | null; temperaturaC: number | null;
      diagnostico: string | null; indicaciones: string | null; proximoControl: string | null;
      formula: Array<{ medicamento: string; presentacion: string | null; dosis: string; via: string | null;
                       frecuencia: string; duracion: string; indicaciones: string | null }>;
      adjuntos: Array<{ id: string; nombre: string; path: string; mime: string | null }>;
    }>;
    vacunas: Array<{ tipo: string; producto: string; lote: string; aplicadaEn: string; proximaDosis: string | null }>;
  } | null;
}

/**
 * Lo que ofrece el negocio de esta cuenta, de cara al cliente: peluquería,
 * clínica, hotel y guardería. La portada lo pintaba fijo y a una veterinaria
 * le anunciaba peluquería, hotel y guardería (14/10/2026).
 */
export function usePortalServicios() {
  return useQuery({
    queryKey: ['portal', 'servicios'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.rpc('portal_servicios_del_negocio');
      if (error) throw error;
      return (data as string[] | null) ?? [];
    },
  });
}

export function usePortalMyPets() {
  return useQuery({
    queryKey: ['portal', 'pets'],
    queryFn: async (): Promise<PortalPetDto[]> => {
      const { data, error } = await supabase.rpc('portal_my_pets');
      if (error) throw error;
      return (data as Array<{
        id: string; name: string; species: string; breed: string | null;
        size: string | null; photo_url: string | null;
        birth_date: string | null; is_sterilized: boolean;
      }>).map((p) => ({
        id: p.id, name: p.name, species: p.species,
        breed: p.breed, size: p.size, photoUrl: p.photo_url,
        birthDate: p.birth_date, isSterilized: p.is_sterilized,
      }));
    },
    staleTime: 30_000,
  });
}

export function usePortalNextReservation() {
  return useQuery({
    queryKey: ['portal', 'nextReservation'],
    queryFn: async (): Promise<PortalNextReservation | null> => {
      const { data, error } = await supabase.rpc('portal_next_reservation');
      if (error) throw error;
      return data as PortalNextReservation | null;
    },
    staleTime: 30_000,
  });
}

export function usePortalPetTimeline(petId: string | undefined) {
  return useQuery({
    queryKey: ['portal', 'timeline', petId],
    enabled: Boolean(petId),
    queryFn: async (): Promise<PortalTimeline> => {
      const { data, error } = await supabase.rpc('portal_pet_timeline', { p_pet_id: petId! });
      if (error) throw error;
      return data as PortalTimeline;
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Tenant's services (a parent needs the catalog to request a booking)
// ═══════════════════════════════════════════════════════════════════════════
export interface PortalCatalogService {
  id: string;
  name: string;
  description: string | null;
  durationMin: number;
  priceBaseCents: number;
  category: string | null;
  /** Precio por tamaño: el portal lo usa según la mascota que elija el dueño. */
  sizePrices?: Partial<Record<PetSize, number>>;
  /** Opciones de precio del servicio (tallas de perro, pelajes de gato). */
  variants?: Array<{ id: string; name: string; priceCents: number; petSize?: PetSize }>;
}

export function usePortalServices() {
  return useQuery({
    queryKey: ['portal', 'services'],
    queryFn: async (): Promise<PortalCatalogService[]> => {
      const { data, error } = await supabase
        .from('grooming_services')
        .select('id,name,description,duration_min,price_base_cents,category,is_active,service_size_prices(size,price_cents),service_variants(id,name,price_cents,pet_size,sort_order,is_active)')
        .is('deleted_at', null)
        .eq('is_active', true)
        .order('category', { nullsFirst: false })
        .order('name');
      if (error) throw error;
      return (data ?? []).map((s) => ({
        id: s.id, name: s.name, description: s.description,
        durationMin: s.duration_min, priceBaseCents: s.price_base_cents,
        category: s.category,
        sizePrices: ((s as unknown as { service_size_prices?: Array<{ size: PetSize; price_cents: number }> })
          .service_size_prices ?? []).reduce<Partial<Record<PetSize, number>>>(
            (acc, sp) => ({ ...acc, [sp.size]: sp.price_cents }), {}),
        variants: ((s as unknown as {
          service_variants?: Array<{
            id: string; name: string; price_cents: number;
            pet_size: PetSize | null; sort_order: number; is_active: boolean;
          }>;
        }).service_variants ?? [])
          .filter((v) => v.is_active)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((v) => ({
            id: v.id, name: v.name, priceCents: v.price_cents,
            petSize: v.pet_size ?? undefined,
          })),
      }));
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Booking requests (parent-side)
// ═══════════════════════════════════════════════════════════════════════════
export interface BookingRequest {
  id: string;
  kind: 'GROOMING' | 'BOARDING' | 'DAYCARE';
  petId: string | null;
  petName: string | null;
  serviceName: string | null;
  preferredStart: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  resolutionNote: string | null;
  depositCents: number;
  depositMethod: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Dónde paga el abono y cuánto
//
// Lo dice el servidor, no la pantalla. El mínimo se comprueba otra vez al
// crear la solicitud: si viviera solo aquí, bastaría con llamar al RPC a mano
// para reservar sin pagar. Ver `Patrón — El precio calculado dos veces`.
// ═══════════════════════════════════════════════════════════════════════════
export interface CuentaDePago {
  id: string;
  kind: string;
  holderName: string;
  number: string;
  notes: string | null;
}

export interface DatosDePago {
  depositRequired: boolean;
  depositMinCents: number;
  accounts: CuentaDePago[];
}

export function usePortalDatosDePago() {
  return useQuery({
    queryKey: ['portal', 'datos-de-pago'],
    queryFn: async (): Promise<DatosDePago> => {
      const { data, error } = await supabase.rpc('portal_datos_de_pago');
      if (error) throw error;
      return (data as DatosDePago | null)
        ?? { depositRequired: false, depositMinCents: 0, accounts: [] };
    },
    staleTime: 5 * 60_000,
  });
}

export function usePortalMyBookings() {
  return useQuery({
    queryKey: ['portal', 'bookings'],
    queryFn: async (): Promise<BookingRequest[]> => {
      const { data, error } = await supabase.rpc('portal_my_booking_requests');
      if (error) throw error;
      return (data as BookingRequest[] | null) ?? [];
    },
    staleTime: 30_000,
  });
}

/**
 * Sube el comprobante y crea la solicitud.
 *
 * Va por la Edge Function y no por el RPC directo por una razón: además de
 * crear la solicitud tiene que salir el correo al salón, y eso no se puede
 * hacer desde SQL. El aviso DENTRO del sistema sí lo escribe el RPC, en la
 * misma transacción, así que una reserva nunca queda sin que nadie se entere
 * aunque el correo falle.
 *
 * El archivo se sube ANTES de crear la solicitud a propósito: si se hiciera
 * al revés y la subida fallara, quedaría una reserva diciendo que hay un
 * comprobante que no existe.
 */
export function usePortalCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: 'GROOMING' | 'BOARDING' | 'DAYCARE';
      petId: string;
      serviceId: string;
      preferredStart: string; // ISO
      preferredEnd?: string;
      notes?: string;
      depositCents?: number;
      depositMethod?: string;
      depositReference?: string;
      comprobante?: File | null;
    }) => {
      const { data: sesion } = await supabase.auth.getSession();
      const token = sesion.session?.access_token;
      const tenantId = sesion.session?.user?.app_metadata?.tenant_id as string | undefined;
      const cuenta = sesion.session?.user?.app_metadata?.portal_account_id as string | undefined;
      if (!token || !tenantId) throw new Error('Tu sesión expiró. Entra de nuevo.');

      // ── El comprobante ──────────────────────────────────────────────
      let depositProofPath: string | null = null;
      if (input.comprobante) {
        // La carpeta del salón primero: es lo que impide subir algo a la de
        // otro, y la política de storage lo comprueba con el mismo criterio.
        const carpeta = cuenta ?? (await carpetaDeLaCuenta());
        const ext = input.comprobante.name.split('.').pop()?.toLowerCase() || 'jpg';
        const ruta = `${tenantId}/${carpeta}/${Date.now()}.${ext}`;
        const { error: errSubida } = await supabase.storage
          .from('booking-proofs')
          .upload(ruta, input.comprobante, { upsert: false });
        if (errSubida) throw errSubida;
        depositProofPath = ruta;
      }

      const resp = await fetch(`${supabaseUrl}/functions/v1/avisar-reserva`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          kind: input.kind,
          petId: input.petId,
          serviceId: input.serviceId,
          preferredStart: input.preferredStart,
          preferredEnd: input.preferredEnd ?? null,
          notes: input.notes ?? null,
          depositCents: input.depositCents ?? 0,
          depositMethod: input.depositMethod ?? null,
          depositReference: input.depositReference ?? null,
          depositProofPath,
        }),
      });

      const datos = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(datos?.error ?? 'No se pudo enviar la solicitud');

      // Un correo que no sale NO es un fallo de la reserva: la reserva está
      // hecha y el salón ya la tiene en su campanita. Se registra para poder
      // diagnosticarlo, y la pantalla no asusta a la clienta con algo que no
      // puede arreglar.
      if (datos?.emailError) {
        console.error('[reserva] el aviso por correo no salió:', datos.emailError);
      }
      return datos as { id: string; emailError: string | null };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'bookings'] });
      qc.invalidateQueries({ queryKey: ['portal', 'notifications'] });
    },
  });
}

/**
 * El id de la cuenta del portal, para la carpeta del comprobante.
 *
 * Normalmente viene en el token; este rodeo es para las cuentas creadas antes
 * de que se añadiera ahí. Sin él, la ruta quedaría mal formada y la política
 * de storage rechazaría la subida sin decir por qué.
 */
async function carpetaDeLaCuenta(): Promise<string> {
  const { data } = await supabase
    .from('customer_portal_accounts')
    .select('id')
    .limit(1)
    .maybeSingle();
  return (data as { id: string } | null)?.id ?? 'sin-cuenta';
}

// ═══════════════════════════════════════════════════════════════════════════
// Cancellation requests
// ═══════════════════════════════════════════════════════════════════════════
export function usePortalRequestCancellation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { appointmentId: string; reason: string }) => {
      const { data, error } = await supabase.rpc('portal_request_cancellation', {
        p_appointment_id: input.appointmentId,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal'] });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Notifications
// ═══════════════════════════════════════════════════════════════════════════
export interface PortalNotification {
  id: string;
  title: string;
  body: string | null;
  kind: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export function usePortalNotifications(unreadOnly = false) {
  return useQuery({
    queryKey: ['portal', 'notifications', unreadOnly],
    queryFn: async (): Promise<PortalNotification[]> => {
      const { data, error } = await supabase.rpc('portal_notifications_list', {
        p_unread_only: unreadOnly,
      });
      if (error) throw error;
      return (data as PortalNotification[] | null) ?? [];
    },
    refetchInterval: 60_000,
  });
}

/**
 * Cuántas notificaciones sin leer tiene el dueño de la mascota, contadas en
 * la base.
 *
 * El contador era el largo de `portal_notifications_list(true)`, que trae
 * como máximo 100: nunca pasaba de ahí y bajaba hasta 100 filas solo para
 * contarlas. Aquí se cuenta con `head: true`, sin bajar ninguna. RLS deja ver
 * solo las de su propia cuenta del portal (`portal_notifications_owner_rw`).
 */
export function usePortalUnreadCount() {
  return useQuery({
    // Bajo ['portal', 'notifications']: marcar como leídas lo invalida.
    queryKey: ['portal', 'notifications', 'sin-leer'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('portal_notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000,
  });
}

export function usePortalMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) => {
      const { error } = await supabase.rpc('portal_notifications_mark_read', {
        p_ids: ids ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'notifications'] }),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Profile update
// ═══════════════════════════════════════════════════════════════════════════
export function usePortalUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      firstName?: string; lastName?: string; phone?: string;
      email?: string; address?: string; city?: string;
    }) => {
      const { data, error } = await supabase.rpc('portal_update_profile', {
        p_first_name: input.firstName ?? null,
        p_last_name: input.lastName ?? null,
        p_phone: input.phone ?? null,
        p_email: input.email ?? null,
        p_address: input.address ?? null,
        p_city: input.city ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal'] }),
  });
}
